import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Activity, ShieldCheck, Network } from 'lucide-react';
const pillars = [{
  icon: Activity,
  title: 'AI Operations Platform',
  for: 'For Organizers',
  desc: 'Automate planning, smart assignments, compliance tracking, and live race-day operations.'
}, {
  icon: ShieldCheck,
  title: 'Workforce Identity',
  for: 'For Volunteers',
  desc: 'Persistent profiles that carry reputation, certifications, and event history across all races.'
}, {
  icon: Network,
  title: 'Event Social Graph',
  for: 'For Community',
  desc: 'A verified talent network where volunteers connect, discover events, and build their portfolio.'
}];
const statCards = [{
  label: 'TOTAL REQUIRED',
  value: '450',
  color: 'text-white'
}, {
  label: 'CONFIRMED',
  value: '442',
  color: 'text-[var(--success)]'
}, {
  label: 'PENDING DOCS',
  value: '18',
  color: 'text-orange-400'
}, {
  label: 'AI CONFIDENCE',
  value: '99%',
  color: 'text-[var(--accent)]'
}];
const assignments = [{
  station: 'Water Station 1',
  time: '06:00 - 10:00',
  filled: '12/12',
  status: 'Ready',
  statusColor: 'text-[var(--success)]'
}, {
  station: 'Finish Line Medical',
  time: '05:30 - 14:00',
  filled: '8/8',
  status: 'Ready',
  statusColor: 'text-[var(--success)]'
}, {
  station: 'Registration Desk',
  time: '04:00 - 10:00',
  filled: '24/25',
  status: 'Pending',
  statusColor: 'text-orange-400'
}, {
  station: 'Route Marshal Zone A',
  time: '06:30 - 12:30',
  filled: '40/40',
  status: 'Ready',
  statusColor: 'text-[var(--success)]'
}, {
  station: 'Parking & Logistics',
  time: '04:00 - 16:00',
  filled: '15/18',
  status: 'Needs Attn',
  statusColor: 'text-[var(--danger)]'
}, {
  station: 'Media Tent',
  time: '07:00 - 15:00',
  filled: '4/4',
  status: 'Ready',
  statusColor: 'text-[var(--success)]'
}];
export function Overview() {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-8" data-id="element-510">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-511">
        <div data-id="element-512">
          <h1 className="text-3xl font-display font-bold text-white mb-1" data-id="element-513">
            Workforce Overview
          </h1>
          <p className="text-[var(--muted)]" data-id="element-514">
            AI allocation complete. 98% roles filled.
          </p>
        </div>
        <button className="bg-[var(--accent)] hover:bg-[#b3e622] text-black px-4 py-2 rounded-md font-semibold text-sm transition-colors self-start sm:self-auto" data-id="element-515">
          Export Briefings
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-516">
        {statCards.map((stat, i) => <motion.div key={stat.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-[var(--surface)] border border-white/5 rounded-xl p-5 flex flex-col justify-between" data-id="element-517">
            <span className="text-xs font-semibold text-[var(--muted)] tracking-wider mb-2 uppercase" data-id="element-518">
              {stat.label}
            </span>
            <span className={`text-4xl font-display font-bold ${stat.color}`} data-id="element-519">
              {stat.value}
            </span>
          </motion.div>)}
      </div>

      {/* Smart Assignments Panel */}
      <div className="bg-[var(--surface)] border border-white/5 rounded-xl overflow-hidden" data-id="element-520">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-521">
          <h2 className="text-sm font-medium text-white" data-id="element-522">Smart Assignments</h2>
          <div className="flex items-center gap-1.5 text-[var(--accent)] text-xs font-medium bg-[var(--accent)]/10 px-2.5 py-1 rounded-full" data-id="element-523">
            <CheckCircle2 className="w-3.5 h-3.5" data-id="element-524" />
            Optimized
          </div>
        </div>

        <div className="divide-y divide-white/5" data-id="element-525">
          {assignments.map((row, i) => <motion.div key={row.station} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3 + i * 0.05
        }} className="px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors group cursor-default" data-id="element-526">
              <div className="flex-1" data-id="element-527">
                <span className="text-sm font-medium text-white group-hover:text-[var(--accent)] transition-colors" data-id="element-528">
                  {row.station}
                </span>
              </div>
              <div className="flex-1 text-center" data-id="element-529">
                <span className="text-sm text-[var(--muted)]" data-id="element-530">{row.time}</span>
              </div>
              <div className="flex-1 text-center" data-id="element-531">
                <span className="text-sm text-[var(--muted)]" data-id="element-532">
                  {row.filled}
                </span>
              </div>
              <div className="flex-1 text-right" data-id="element-533">
                <span className={`text-sm font-medium ${row.statusColor}`} data-id="element-534">
                  {row.status}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* More Than Just Software */}
      <section data-id="element-535">
        <div className="mb-6" data-id="element-536">
          <h2 className="font-display font-bold text-2xl text-white mb-1" data-id="element-537">
            More Than Just Software
          </h2>
          <p className="text-[var(--muted)] text-sm" data-id="element-538">
            A complete ecosystem designed to solve the event workforce problem
            from every angle.
          </p>
          <p className="text-[var(--fg)] text-base mt-3 max-w-2xl" data-id="element-539">
            Reduce race planning from 30 days to a few hours. Build a persistent
            identity and reputation network for volunteers across all your
            events.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-540">
          {pillars.map((pillar, i) => <motion.div key={pillar.title} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4 + i * 0.1
        }} className="bg-[var(--surface)] border border-white/5 rounded-xl p-6 hover:border-[var(--accent)]/30 transition-colors" data-id="element-541">
              <div className="w-11 h-11 bg-[var(--accent)]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-542">
                <pillar.icon className="w-5 h-5 text-[var(--accent)]" data-id="element-543" />
              </div>
              <span className="text-[var(--accent)] text-xs font-bold tracking-wider uppercase mb-1.5 block" data-id="element-544">
                {pillar.for}
              </span>
              <h3 className="font-display font-bold text-lg text-white mb-2" data-id="element-545">
                {pillar.title}
              </h3>
              <p className="text-[var(--muted)] text-sm leading-relaxed" data-id="element-546">
                {pillar.desc}
              </p>
            </motion.div>)}
        </div>
      </section>
    </motion.div>;
}